/**
 * Dynamic text (dates, times, ids) that legitimately differs between a baseline and a
 * later capture. A text run is ignored only when it has the same shape on both sides: its
 * dynamic parts are swapped for placeholders and the rest must read identically. A date
 * that became "ERROR", or "Updated" that became "Deleted", changes the shape and still shows.
 */
import { VISUAL_TEXT_CONTAINERS, VISUAL_TEXT_LANDMARKS, type VisualAutoIgnore, type VisualRect, type VisualTextRun } from './visual-review.js';
import { z } from 'zod';

// Whole month names or exact abbreviations only: "Decide 5" and "Marketing 2" are not dates.
const MONTH =
  '(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|June?|July?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\\.?';

// Per-run sandbox hosts (`<random>.sbx.runcofactor.com`, dev `<random>.sbx.dev.runcofactor.com`),
// see `previewDomain` in infra/components/blackcat-dev.constants.ts. Only the varying subdomain is
// replaced; the suffix stays literal, so a different registrable domain changes the shape.
const SANDBOX_HOST_SUFFIX = '\\.sbx(?:\\.dev)?\\.runcofactor\\.com(?![\\w-]|\\.[\\w-])';

/** Order matters: earlier patterns consume text before later ones see it. */
export const DYNAMIC_PATTERNS: ReadonlyArray<{ kind: string; pattern: RegExp }> = [
  // First: a random label can look like a hex id or a date and must not be split by those patterns.
  {
    kind: 'hostname',
    pattern: new RegExp(
      `(?<![\\w.-])[a-z0-9][a-z0-9-]*(?:\\.[a-z0-9-]+)*(?=${SANDBOX_HOST_SUFFIX})`,
      'gi'
    ),
  },
  {
    kind: 'uuid',
    pattern: /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
  },
  // Prefixed entity ids (`exec_<22 base58 chars>`), see docs/project/prefixed-ids.md.
  { kind: 'id', pattern: /\b[a-z]{2,6}_[1-9A-HJ-NP-Za-km-z]{20,24}\b/g },
  // Hex digests and dashless uuids: needs a letter, so a long bare number is not an id.
  { kind: 'id', pattern: /\b(?=[0-9a-f]*[a-f])[0-9a-f]{16,}\b/gi },
  {
    kind: 'date',
    pattern:
      /\b\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?(?!\d)/g,
  },
  // 10/14/2026 or 14.10.2026; a bare 2.14.1 is a version, not a date.
  { kind: 'date', pattern: /\b\d{1,2}(?:\/\d{1,2}\/\d{2,4}|\.\d{1,2}\.\d{4})\b/g },
  {
    kind: 'date',
    pattern: new RegExp(`\\b${MONTH}\\s+\\d{1,2}(?:st|nd|rd|th)?(?:,?\\s+\\d{4})?\\b`, 'g'),
  },
  {
    kind: 'date',
    pattern: new RegExp(`\\b\\d{1,2}(?:st|nd|rd|th)?\\s+${MONTH}(?:,?\\s+\\d{4})?\\b`, 'g'),
  },
  // "5 minutes ago", plus the qualified forms date-fns style libraries print: "less than a minute
  // ago", "about an hour ago", "almost 2 years ago". Without "ago" a phrase is plain text.
  {
    kind: 'relative-time',
    pattern:
      /\b(?:just now|(?:(?:less than|about|almost|over|under|more than)\s+)?(?:an?|\d+)\s*(?:s|secs?|seconds?|m|mins?|minutes?|h|hrs?|hours?|d|days?|w|wks?|weeks?|mo|months?|y|yrs?|years?)\s+ago)\b/gi,
  },
  {
    kind: 'time',
    pattern:
      /\b\d{1,2}:\d{2}(?::\d{2})?(?:\s?[AaPp]\.?[Mm]\.?)?(?:\s?(?:UTC|GMT|[ECMP][SD]T))?(?![\dA-Za-z])/g,
  },
  // Elapsed time as "1m 23s", "1h 2m", "45s", "3m", and measured seconds with two or more
  // decimals ("50.291s", "1m 26.83s"). Hours need minutes ("1h 2m"; a lone "5h" is plain text).
  // Bare seconds must be under 60, so a one-decimal "2.5s" and 60s-90s decades stay plain text;
  // bare minutes are a run's elapsed time ("Duration 3m"), except after a currency sign ("$5m").
  // Known limits: "10s"-"50s" read as seconds, so "the 40s" becoming "the 50s" is ignored, and a
  // distance in metres ("5m") reads as minutes; "1m 23s ago" is taken by relative-time first.
  // "0:05" is the time pattern.
  {
    kind: 'duration',
    pattern:
      /(?<![\w.])(?:\d{1,3}h\s?[1-5]?\dm(?:\s?[1-5]?\ds)?|\d{1,3}m\s?(?:[1-5]?\d\.\d{2,}|[1-5]?\d)s|\d{1,3}\.\d{2,}s|[1-5]?\ds|(?<![$€£¥])\d{1,3}m)(?![\w])/g,
  },
  // Name tokens that end in a millisecond timestamp (`Date.now()`: 13 digits starting with 1, as in
  // "Example-Run-1792065674902"). A bare 13-digit number with no letter and separator before it is
  // a real number.
  { kind: 'id', pattern: /(?<=[A-Za-z][-_])1\d{12}(?!\d)/g },
  // Random 8-hex suffix of a generated mailbox ("qa-teammate-b2c0299e@example.com"). Needs a separator
  // before it and a letter in it, so "deadbeef@example.com" and "user-12345678@example.com" stay.
  { kind: 'id', pattern: /(?<=[A-Za-z0-9][-_+.])(?=[0-9a-f]{0,7}[a-f])[0-9a-f]{8}(?=@)/gi },
  // Random alphanumeric mailbox suffix of 8-12 characters ("test-k3j2h1p0@example.com"). Only before "@",
  // and only when letters and digits alternate at least three times, so "john.smith1990@" and
  // "gamer-99xx@" (a name plus a number) stay. The second lookahead reads: letter, digits, letters,
  // digit (or digit, letters, digits, letter), i.e. at least four alternating runs.
  {
    kind: 'id',
    pattern:
      /(?<=[A-Za-z0-9][-_+.])(?=[a-z0-9]{8,12}@)(?=[a-z0-9]*?(?:[a-z]\d+[a-z]+\d|\d[a-z]+\d+[a-z]))[a-z0-9]{8,12}(?=@)/gi,
  },
  // Short ids: a run id, a commit ("2e04132d", "cfff25a"), 6-12 hex characters of one case with a
  // digit and a letter, as a token of its own (not joined to a word by "_", part of a longer hex
  // run, or a mailbox, whose patterns above decide). A number with no letter, or a word of hex letters ("facade"), is
  // not one.
  {
    kind: 'id',
    pattern:
      /(?<![\p{L}\p{N}_.])(?:(?=[0-9a-f]{0,11}\d)(?=[0-9a-f]{0,11}[a-f])[0-9a-f]{6,12}|(?=[0-9A-F]{0,11}\d)(?=[0-9A-F]{0,11}[A-F])[0-9A-F]{6,12})(?![\p{L}\p{N}_@])/gu,
  },
];

type DynamicShape = { skeleton: string; kinds: string[] };

/** The text with each dynamic part replaced by `<kind>`, and the kinds found, in order of the patterns. */
function dynamicShape(text: string): DynamicShape {
  const kinds: string[] = [];
  let skeleton = text;
  for (const { kind, pattern } of DYNAMIC_PATTERNS) {
    skeleton = skeleton.replace(pattern, () => {
      kinds.push(kind);
      return `<${kind}>`;
    });
  }
  return { skeleton: skeleton.replace(/\s+/g, ' ').trim(), kinds };
}

/** Rects this close (px) still count as the same spot: a longer value shifts its own right edge. */
const SAME_SPOT_PAD = 8;

/**
 * Overlap of the two rects once each is grown by `SAME_SPOT_PAD`; 0 when they do not touch.
 * Vertically that pad on each rect is the tolerance for a run that moved: about a line height
 * plus 16px, so content above growing by a line or two still pairs.
 */
const overlapArea = (a: VisualRect, b: VisualRect) => {
  const w =
    Math.min(a.x + a.width, b.x + b.width) + SAME_SPOT_PAD - (Math.max(a.x, b.x) - SAME_SPOT_PAD);
  const h =
    Math.min(a.y + a.height, b.y + b.height) + SAME_SPOT_PAD - (Math.max(a.y, b.y) - SAME_SPOT_PAD);
  return w > 0 && h > 0 ? w * h : 0;
};

/** Text boxes that only touch, or overlap by rounding, are neighbours, not occupants: "Updated:" and its value. */
const TOUCH_TOLERANCE = 2;

const intersects = (a: VisualRect, b: VisualRect) =>
  a.x + TOUCH_TOLERANCE < b.x + b.width &&
  b.x + TOUCH_TOLERANCE < a.x + a.width &&
  a.y + TOUCH_TOLERANCE < b.y + b.height &&
  b.y + TOUCH_TOLERANCE < a.y + a.height;

const union = (a: VisualRect, b: VisualRect): VisualRect => {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return {
    x,
    y,
    width: Math.max(a.x + a.width, b.x + b.width) - x,
    height: Math.max(a.y + a.height, b.y + b.height) - y,
  };
};

type Shaped = DynamicShape & { run: VisualTextRun };

/** Remove and return the unused baseline run with the same shape that sits closest to `current`. */
function takeBestPartner(available: Shaped[], current: Shaped): Shaped | null {
  let best = -1;
  let bestArea = 0;
  for (const [index, candidate] of available.entries()) {
    if (candidate.skeleton !== current.skeleton) continue;
    const area = overlapArea(candidate.run.rect, current.run.rect);
    if (area > bestArea) {
      best = index;
      bestArea = area;
    }
  }
  return best < 0 ? null : (available.splice(best, 1)[0] ?? null);
}

/**
 * Text on the same line, to the right of a value whose width changed, that kept its words and
 * moved sideways by that change: the value pushed it, nothing about it changed. Returns those runs
 * (both sides), which no longer count as crowding the value, and the box of each separator among
 * them, which the diff leaves out.
 */
function shiftedNeighbours(
  baseline: VisualTextRun[],
  capture: VisualTextRun[],
  before: VisualTextRun,
  after: VisualTextRun
): { runs: Set<VisualTextRun>; rects: VisualRect[] } {
  const runs = new Set<VisualTextRun>();
  const rects: VisualRect[] = [];
  const widthChange = after.rect.width - before.rect.width;
  if (Math.abs(widthChange) <= TOUCH_TOLERANCE) return { runs, rects };
  const sameLine = (a: VisualRect, b: VisualRect) =>
    Math.abs(a.y - b.y) <= TOUCH_TOLERANCE && Math.abs(a.height - b.height) <= TOUCH_TOLERANCE;
  const unused = new Set(capture);
  for (const old of baseline) {
    if (
      old === before ||
      !sameLine(old.rect, before.rect) ||
      old.rect.x < before.rect.x + before.rect.width - TOUCH_TOLERANCE
    )
      continue;
    const moved = [...unused].find(
      (candidate) =>
        candidate !== after &&
        candidate.text === old.text &&
        sameLine(candidate.rect, old.rect) &&
        Math.abs(candidate.rect.x - old.rect.x - widthChange) <= 1
    );
    if (!moved) continue;
    unused.delete(moved);
    runs.add(old).add(moved);
    // Only a run with no letter or digit ("·", "|", an icon glyph) is left out of the diff. A word
    // that slid keeps being compared as before, so a pill that changed colour or style is still seen,
    // and one that merely slid is flagged exactly as it was before this change.
    if (!/[\p{L}\p{N}]/u.test(old.text)) rects.push(union(old.rect, moved.rect));
  }
  return { runs, rects };
}

/** What happened to one capture run that contains a dynamic value. */
export type DynamicRunOutcome =
  | {
      status: 'ignored';
      kind: string;
      rect: VisualRect;
      before: string;
      after: string;
      /** Boxes of separators (text with no letter or digit) that only slid by the value's change in width. */
      shifted: VisualRect[];
    }
  /** The same text sat in the same spot before: nothing changed there, so nothing is left out. */
  | { status: 'identical'; text: string }
  /** No unused baseline run of the same shape sits at this spot, so the change counts. */
  | { status: 'no_partner'; text: string; skeleton: string }
  /** A partner exists, but other text sits inside the combined box, so the change counts. */
  | { status: 'crowded'; before: string; after: string; rect: VisualRect };

/** One outcome per capture run that contains a dynamic value, in document order. */
export function explainDynamicRuns(
  baseline: VisualTextRun[],
  capture: VisualTextRun[]
): DynamicRunOutcome[] {
  const shaped = (runs: VisualTextRun[]) =>
    runs.flatMap((run) => {
      const shape = dynamicShape(run.text);
      return shape.kinds.length > 0 ? [{ run, ...shape }] : [];
    });
  const available = shaped(baseline);
  return shaped(capture).map((current): DynamicRunOutcome => {
    const partner = takeBestPartner(available, current);
    if (!partner)
      return { status: 'no_partner', text: current.run.text, skeleton: current.skeleton };
    if (partner.run.text === current.run.text)
      return { status: 'identical', text: current.run.text };
    const rect = union(partner.run.rect, current.run.rect);
    const shifts = shiftedNeighbours(baseline, capture, partner.run, current.run);
    // The box also covers whatever else sits there on either side. Text that appeared where a
    // shorter value used to end is caught here; icons, images and other non-text content are not.
    // Text that only slid sideways with the value ("·", "Passed" after a longer date) is not new.
    const crowded = [...baseline, ...capture].some(
      (other) =>
        other !== partner.run &&
        other !== current.run &&
        !shifts.runs.has(other) &&
        intersects(other.rect, rect)
    );
    return crowded
      ? { status: 'crowded', before: partner.run.text, after: current.run.text, rect }
      : {
          status: 'ignored',
          kind: [...new Set(current.kinds)].join('+'),
          rect,
          before: partner.run.text,
          after: current.run.text,
          shifted: shifts.rects,
        };
  });
}

/**
 * Regions to leave out of the pixel diff: capture runs whose text differs from a baseline run in
 * the same spot only inside dynamic parts. A run with no partner, or whose partner has a different
 * shape, is not returned, so whatever changed there is compared as usual. Identical text is never
 * returned, so an unrelated change under a stable date still shows.
 */
export function pairDynamicRuns(
  baseline: VisualTextRun[],
  capture: VisualTextRun[]
): VisualAutoIgnore[] {
  return explainDynamicRuns(baseline, capture).flatMap((outcome) =>
    outcome.status === 'ignored'
      ? [
          { kind: outcome.kind, rect: outcome.rect },
          ...outcome.shifted.map((rect) => ({ kind: 'shift', rect })),
        ]
      : []
  );
}

/* ── Ignore rules ────────────────────────────────────────────────────────── */

const VISUAL_TEXT_LIMITS = {
  maxRuns: 400,
  maxChars: 200,
  maxVisitedNodes: 5_000,
  /** Text wrapped over more lines than this is a paragraph, not a value. */
  maxLines: 4,
  maxCoordinate: 100_000,
} as const;

const isBoundedRect = (value: unknown) => {
  if (typeof value !== 'object' || value === null) return false;
  const rect = value as Record<string, unknown>;
  const bounded = (key: string) =>
    typeof rect[key] === 'number' &&
    Number.isFinite(rect[key]) &&
    Math.abs(rect[key]) <= VISUAL_TEXT_LIMITS.maxCoordinate;
  return (
    bounded('x') &&
    bounded('y') &&
    bounded('width') &&
    bounded('height') &&
    (rect.width as number) > 0 &&
    (rect.height as number) > 0
  );
};

/**
 * What a context fact may look like. The page script below builds its output from these same
 * values, so a well-behaved page always passes; only forged output fails, and then the whole read
 * is refused like any other malformed output.
 */
const CONTEXT_TAG = /^[a-z][a-z0-9-]{0,31}$/;
const CONTEXT_ROLE = /^[a-z]{1,32}$/;
const CONTAINERS: ReadonlySet<string> = new Set(VISUAL_TEXT_CONTAINERS);
const LANDMARKS: ReadonlySet<string> = new Set(VISUAL_TEXT_LANDMARKS);

const isBoundedContext = (value: unknown) => {
  if (typeof value !== 'object' || value === null) return false;
  const { tag, role, level, container, landmark } = value as Record<string, unknown>;
  return (
    typeof tag === 'string' &&
    CONTEXT_TAG.test(tag) &&
    (role === undefined || (typeof role === 'string' && CONTEXT_ROLE.test(role))) &&
    (level === undefined || (Number.isInteger(level) && (level as number) >= 1 && (level as number) <= 6)) &&
    (container === undefined || (typeof container === 'string' && CONTAINERS.has(container))) &&
    (landmark === undefined || (typeof landmark === 'string' && LANDMARKS.has(landmark)))
  );
};

export const isVisualTextRuns = (value: unknown): value is VisualTextRun[] =>
  Array.isArray(value) &&
  value.length <= VISUAL_TEXT_LIMITS.maxRuns &&
  value.every(
    (run) =>
      typeof run === 'object' &&
      run !== null &&
      typeof (run as Record<string, unknown>).text === 'string' &&
      ((run as Record<string, unknown>).text as string).length <= VISUAL_TEXT_LIMITS.maxChars &&
      isBoundedRect((run as Record<string, unknown>).rect) &&
      isBoundedContext((run as Record<string, unknown>).context)
  );

/**
 * A function expression, not an invoked one: both browser clients call `(${fn})()` themselves.
 * Reads only; the page the agent keeps driving is left exactly as it was. Rects are scaled by the
 * device pixel ratio because the screenshot is taken in device pixels.
 */
export const VISUAL_TEXT_RUNS_SCRIPT = `(cssPixels = false) => {
  const runs = [];
  // Generic DOM facts per run (see VisualTextContext), read on one walk up from the text's element.
  const landmarkRoles = new Set(${JSON.stringify(VISUAL_TEXT_LANDMARKS)});
  const landmarkTags = { NAV: 'navigation', MAIN: 'main', ASIDE: 'complementary', DIALOG: 'dialog', HEADER: 'banner', FOOTER: 'contentinfo' };
  // A header or footer inside one of these belongs to that section, not to the page.
  const sectioning = new Set(['ARTICLE', 'ASIDE', 'MAIN', 'NAV', 'SECTION']);
  const roleTags = { BUTTON: 'button', TH: 'columnheader', TD: 'cell', LI: 'listitem', OPTION: 'option', INPUT: 'textbox', TEXTAREA: 'textbox', SELECT: 'combobox', H1: 'heading', H2: 'heading', H3: 'heading', H4: 'heading', H5: 'heading', H6: 'heading' };
  const tableRoles = new Set(['table', 'grid', 'treegrid']);
  const listRoles = new Set(['list', 'listbox', 'menu', 'menubar', 'tree', 'tablist', 'radiogroup']);
  const tagPattern = ${CONTEXT_TAG};
  const rolePattern = ${CONTEXT_ROLE};
  const inSectioning = (el) => {
    for (let up = el.parentElement; up && up !== document.body; up = up.parentElement)
      if (sectioning.has(up.tagName)) return true;
    return false;
  };
  const tagLandmark = (el) =>
    (el.tagName === 'HEADER' || el.tagName === 'FOOTER') && inSectioning(el) ? undefined : landmarkTags[el.tagName];
  const describe = (start) => {
    const tag = start.tagName.toLowerCase();
    const context = { tag: tagPattern.test(tag) ? tag : 'unknown' };
    for (let el = start; el && el !== document.body; el = el.parentElement) {
      const token = (el.getAttribute('role') || '').trim().split(/\\s+/)[0].toLowerCase();
      const explicit = token === 'alertdialog' ? 'dialog' : token;
      const landmark = landmarkRoles.has(explicit) ? explicit : explicit ? undefined : tagLandmark(el);
      if (landmark) {
        context.landmark = landmark;
        break;
      }
      if (!context.container) {
        if (tableRoles.has(explicit) || (!explicit && el.tagName === 'TABLE')) context.container = 'table';
        else if (listRoles.has(explicit) || (!explicit && ['UL', 'OL', 'DL', 'MENU'].includes(el.tagName))) context.container = 'list';
      }
      if (context.role || explicit === 'presentation' || explicit === 'none') continue;
      // An explicit role that is not a plain word (doc-*, a typo with digits) gives no role at all.
      const implicit = el.tagName === 'A' ? (el.getAttribute('href') === null ? undefined : 'link') : roleTags[el.tagName];
      const found = explicit ? (rolePattern.test(explicit) ? explicit : undefined) : implicit;
      if (!found) continue;
      context.role = found;
      if (found !== 'heading') continue;
      const level = /^H[1-6]$/.test(el.tagName) ? Number(el.tagName[1]) : Number(el.getAttribute('aria-level'));
      if (Number.isInteger(level) && level >= 1 && level <= 6) context.level = level;
    }
    return context;
  };
  const skip = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA']);
  const scale = cssPixels ? 1 : (window.devicePixelRatio || 1);
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  let visited = 0;
  for (
    let node = walker.nextNode();
    node && runs.length < ${VISUAL_TEXT_LIMITS.maxRuns} && visited < ${VISUAL_TEXT_LIMITS.maxVisitedNodes};
    node = walker.nextNode()
  ) {
    visited++;
    const text = (node.nodeValue || '').replace(/\\s+/g, ' ').trim();
    const el = node.parentElement;
    if (!text || text.length > ${VISUAL_TEXT_LIMITS.maxChars} || !el || skip.has(el.tagName)) continue;
    if (!el.checkVisibility({ opacityProperty: true, visibilityProperty: true })) continue;
    range.selectNodeContents(node);
    // Wrapped text is one run whose box spans its lines; pairDynamicRuns refuses a box that
    // also covers other text. A paragraph (more lines than a value wraps over) is skipped.
    const lines = range.getClientRects().length;
    if (lines < 1 || lines > ${VISUAL_TEXT_LIMITS.maxLines}) continue;
    const r = range.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0 || r.bottom <= 0 || r.right <= 0) continue;
    if (r.top >= window.innerHeight || r.left >= window.innerWidth) continue;
    // Text clipped by its element (ellipsis, overflow hidden) has a layout box wider than what shows.
    const box = el.getBoundingClientRect();
    if (r.right > box.right + 1 || r.bottom > box.bottom + 1 || r.left < box.left - 1 || r.top < box.top - 1) continue;
    // Text under a modal, drawer or sticky bar is not what the screenshot shows at that spot.
    const hit = document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2);
    if (!hit || !(el.contains(hit) || hit.contains(el))) continue;
    runs.push({
      text,
      rect: {
        x: Math.floor(r.left * scale),
        y: Math.floor(r.top * scale),
        width: Math.ceil(r.width * scale),
        height: Math.ceil(r.height * scale),
      },
      context: describe(el),
    });
  }
  // What was typed into a field is not a text node. Passwords, card and secret-looking fields, and
  // non-text inputs are never read.
  const readable = new Set(['text', 'email', 'search', 'url', 'tel', 'number']);
  for (const field of document.querySelectorAll('input, textarea')) {
    if (runs.length >= ${VISUAL_TEXT_LIMITS.maxRuns}) break;
    if (field.tagName === 'INPUT' && !readable.has(field.type)) continue;
    // A show-password toggle turns a password field into type=text; card and one-time-code fields too.
    if (/cc-|password|one-time-code/i.test(field.autocomplete || '')) continue;
    if (/pass|pwd|secret|token|otp|cvv|cvc|card/i.test((field.name || '') + ' ' + (field.id || ''))) continue;
    const text = (field.value || '').replace(/\\s+/g, ' ').trim();
    if (!text || text.length > ${VISUAL_TEXT_LIMITS.maxChars}) continue;
    if (!field.checkVisibility({ opacityProperty: true, visibilityProperty: true })) continue;
    const r = field.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0 || r.bottom <= 0 || r.right <= 0) continue;
    if (r.top >= window.innerHeight || r.left >= window.innerWidth) continue;
    const hit = document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2);
    if (hit !== field) continue;
    runs.push({
      text,
      rect: {
        x: Math.floor(r.left * scale),
        y: Math.floor(r.top * scale),
        width: Math.ceil(r.width * scale),
        height: Math.ceil(r.height * scale),
      },
      context: describe(field),
    });
  }
  return JSON.stringify(runs);
}`;

/** Payload returned by the anchor script, shared by target resolution and capture. */
export const VisualAnchorCaptureSchema = z.object({
  elements: z.array(z.object({
    index: z.number().int().nonnegative(),
    rect: z.object({ x: z.number(), y: z.number(), width: z.number().positive(), height: z.number().positive() }),
    locator: z.string().min(1),
    label: z.string(),
  })).max(400),
  containers: z.array(z.object({
    label: z.string(),
    kind: z.string(),
    candidateLocators: z.array(z.string().min(1)),
  })).max(100),
});
export type VisualAnchorCapture = z.infer<typeof VisualAnchorCaptureSchema>;

/** Collects stable anchors from one page walk; all text/media locators are unique in the DOM. */
export const VISUAL_ANCHORS_SCRIPT = `() => {
  const generatedId = /^(?:[0-9]+|[a-f0-9]{8,}|radix-|headlessui-|react-aria-|:r[0-9a-z]+:)$/i;
  const cssEscape = (value) => CSS.escape(value);
  const cssPath = (el) => {
    const parts = [];
    for (let node = el; node && node.nodeType === 1 && node !== document.body; node = node.parentElement) {
      let part = node.tagName.toLowerCase();
      const siblings = node.parentElement ? [...node.parentElement.children].filter((child) => child.tagName === node.tagName) : [];
      if (siblings.length > 1) part += ':nth-of-type(' + (siblings.indexOf(node) + 1) + ')';
      parts.unshift(part);
      const candidate = parts.join(' > ');
      try { if (document.querySelectorAll(candidate).length === 1) return candidate; } catch { /* Keep walking toward a valid unique path. */ }
    }
    return parts.join(' > ');
  };
  const accessibleName = (el) => {
    const labelledBy = el.getAttribute('aria-labelledby');
    if (labelledBy) {
      const name = labelledBy.split(/\\s+/).map((id) => document.getElementById(id)?.innerText || '').join(' ').trim();
      if (name) return name;
    }
    return (el.getAttribute('aria-label') || el.getAttribute('alt') || el.getAttribute('title') || '').replace(/\\s+/g, ' ').trim().slice(0, 120);
  };
  const roleOf = (el) => el.getAttribute('role') || ({ BUTTON: 'button', A: el.hasAttribute('href') ? 'link' : '', IMG: 'img', TABLE: 'table', FORM: 'form', NAV: 'navigation', MAIN: 'main', DIALOG: 'dialog', UL: 'list', OL: 'list' }[el.tagName] || '');
  const rectOf = (el) => {
    const r = el.getBoundingClientRect();
    return { x: Math.floor(r.left), y: Math.floor(r.top), width: Math.ceil(r.width), height: Math.ceil(r.height) };
  };
  const uniqueCss = (selector) => { try { return document.querySelectorAll(selector).length === 1; } catch { return false; } };
  const candidates = (el) => {
    const out = [];
    const testId = el.getAttribute('data-testid');
    if (testId) out.push('[data-testid="' + CSS.escape(testId) + '"]');
    if (el.id && !generatedId.test(el.id)) out.push('#' + cssEscape(el.id));
    const role = roleOf(el), name = accessibleName(el);
    if (role && name) out.push('role=' + role + '[name=' + JSON.stringify(name) + ']');
    out.push(cssPath(el));
    return [...new Set(out)];
  };
  const rendered = (el) => {
    if (!el.checkVisibility({ opacityProperty: true, visibilityProperty: true })) return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  };
  const inViewport = (el) => {
    const r = el.getBoundingClientRect();
    return r.bottom > 0 && r.right > 0 && r.top < innerHeight && r.left < innerWidth;
  };
  const visible = (el) => rendered(el) && inViewport(el);
  const elements = [];
  for (const el of document.querySelectorAll('body *')) {
    if (elements.length >= 400 || !visible(el)) continue;
    const media = /^(IMG|CANVAS|SVG|VIDEO|IFRAME)$/.test(el.tagName);
    const hasDirectText = [...el.childNodes].some((node) => node.nodeType === Node.TEXT_NODE && (node.nodeValue || '').trim());
    if (!media && !hasDirectText) continue;
    const label = accessibleName(el) || (el.innerText || '').replace(/\\s+/g, ' ').trim().slice(0, 160) || el.tagName.toLowerCase();
    const locator = candidates(el).find((candidate) => !candidate.startsWith('role=') && uniqueCss(candidate));
    if (!locator) continue;
    elements.push({ index: elements.length, rect: rectOf(el), locator, label });
  }
  const containers = [];
  const semanticSelector = 'table,[role="table"],[role="grid"],section,article,form,[role="dialog"],dialog,ul,ol,[role="list"],[role="region"],main,nav,aside,[role="main"],[role="navigation"],[role="complementary"]';
  const semantic = [...document.querySelectorAll(semanticSelector)];
  const cards = [...document.querySelectorAll('[data-testid],[class],[role="group"]')].filter((el) => {
    const identity = (el.getAttribute('data-testid') || '') + ' ' + String(el.className || '');
    const hasHeading = el.matches('div') && el.querySelector(':scope > h1,:scope > h2,:scope > h3,:scope > h4,:scope > h5,:scope > h6');
    return /card/i.test(identity) || el.getAttribute('role') === 'group' || hasHeading;
  });
  const seenContainers = new Set();
  for (const el of [...semantic, ...cards]) {
    if (containers.length >= 100 || seenContainers.has(el) || !rendered(el)) continue;
    seenContainers.add(el);
    const role = roleOf(el), kind = role || el.tagName.toLowerCase();
    const heading = el.querySelector('h1,h2,h3,h4,h5,h6')?.innerText || '';
    const label = accessibleName(el) || heading.replace(/\\s+/g, ' ').trim() || (el.innerText || '').replace(/\\s+/g, ' ').trim().slice(0, 80) || kind;
    containers.push({ label: kind + ': ' + label, kind, candidateLocators: candidates(el) });
  }
  return JSON.stringify({ elements, containers });
}`;
